import React, { useState, useEffect } from 'react';
import {
  Button,
  TextInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Calendar,
  Popover,
} from '@c2fo/liquidity';
import { CalendarIcon } from '@c2fo/liquidity/icons';
import { format } from 'date-fns';
import { LoadingOverlay } from '../LoadingSpinner';
import { ListViewSwitch } from '../ListViewSwitch';
import { CredentialTable } from '../CredentialTable';
import { CredentialGrid } from '../CredentialGrid';
import { useCredentialsState } from '../../contexts';
import type { CredentialsSortField } from '../../contexts/FileDeliveryContext';
import { CredentialType } from '../../api/shared';
import { SavedSearches } from '../SavedSearches';
import { useSavedSearches } from '../../hooks';
import { Pagination } from '../Pagination';
import { PageHeader } from '../PageHeader';

const FILTER_FIELDS = [
  { value: 'display_name', label: 'Display Name', type: 'text' as const },
  { value: 'name', label: 'Name', type: 'text' as const },
  { value: 'credential_type', label: 'Type', type: 'select' as const },
  { value: 'create_time', label: 'Created', type: 'date' as const },
  { value: 'update_time', label: 'Updated', type: 'date' as const },
];

const DATE_OPERATORS = [
  { value: '>', label: 'After' },
  { value: '<', label: 'Before' },
  { value: '=', label: 'Equals' },
  { value: 'between', label: 'Between' },
];

const CREDENTIAL_TYPE_OPTIONS = [
  { value: CredentialType.CREDENTIAL_TYPE_SSH, label: 'SFTP' },
  { value: CredentialType.CREDENTIAL_TYPE_AWS, label: 'AWS S3' },
  { value: CredentialType.CREDENTIAL_TYPE_GCP_SERVICE_ACCOUNT, label: 'GCP Storage' },
  { value: CredentialType.CREDENTIAL_TYPE_AZURE_SERVICE_PRINCIPAL, label: 'Azure Blob' },
];

const SORT_OPTIONS = [
  { value: 'display_name:asc', label: 'Name (A-Z)' },
  { value: 'display_name:desc', label: 'Name (Z-A)' },
  { value: 'credential_type:asc', label: 'Type (A-Z)' },
  { value: 'credential_type:desc', label: 'Type (Z-A)' },
  { value: 'create_time:desc', label: 'Created (Newest)' },
  { value: 'create_time:asc', label: 'Created (Oldest)' },
  { value: 'update_time:desc', label: 'Updated (Newest)' },
  { value: 'update_time:asc', label: 'Updated (Oldest)' },
];

export const CredentialsListView: React.FC = () => {
  const [filterField, setFilterField] = useState('display_name');
  const [filterValue, setFilterValue] = useState('');
  const [dateOperator, setDateOperator] = useState('>');
  const [dateValue, setDateValue] = useState<Date | undefined>(undefined);
  const [dateValueEnd, setDateValueEnd] = useState<Date | undefined>(undefined);
  const [selectValue, setSelectValue] = useState('');
  const { savedSearches, saveSearch, deleteSearch, isNameTaken } = useSavedSearches('credentials');
  const {
    credentials,
    isLoading,
    currentPage,
    totalPages,
    totalSize,
    pageSize,
    hasNextPage,
    hasPreviousPage,
    fetchCredentials,
    setPageSize,
    setFilter,
    filter,
    setSort,
    sortField,
    sortDirection,
    setViewMode,
    viewMode,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    activeSearchName,
    setActiveSearchName,
  } = useCredentialsState();

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const handleApplyFilter = () => {
    const field = FILTER_FIELDS.find((f) => f.value === filterField);
    let newFilter = '';

    if (field?.type === 'text' && filterValue.trim()) {
      newFilter = `${filterField}:"${filterValue.trim()}"`;
    } else if (field?.type === 'select' && selectValue) {
      newFilter = `${filterField}:"${selectValue}"`;
    } else if (field?.type === 'date' && dateValue) {
      const formattedDate = format(dateValue, "yyyy-MM-dd'T'HH:mm:ss'Z'");
      if (dateOperator === 'between' && dateValueEnd) {
        const formattedDateEnd = format(dateValueEnd, "yyyy-MM-dd'T'HH:mm:ss'Z'");
        newFilter = `${filterField} >= "${formattedDate}" AND ${filterField} <= "${formattedDateEnd}"`;
      } else {
        newFilter = `${filterField} ${dateOperator} "${formattedDate}"`;
      }
    }

    setFilter(newFilter || undefined);
    setActiveSearchName(undefined);
  };

  const handleClearFilter = () => {
    setFilterValue('');
    setSelectValue('');
    setDateValue(undefined);
    setDateValueEnd(undefined);
    setFilter(undefined);
    setActiveSearchName(undefined);
  };

  useEffect(() => {
    if (filter !== undefined) {
      fetchCredentials();
    }
  }, [filter, fetchCredentials]);

  const handleFieldChange = (value: string) => {
    setFilterField(value);
    setFilterValue('');
    setSelectValue('');
    setDateValue(undefined);
    setDateValueEnd(undefined);
  };

  const selectedField = FILTER_FIELDS.find((f) => f.value === filterField);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <PageHeader title="Credentials" description="Manage credentials and credential methods" />
        <LoadingOverlay message="Loading credentials..." />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader title="Credentials" description="Manage credentials and credential methods" />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Select value={filterField} onValueChange={handleFieldChange}>
              <SelectTrigger className="w-36" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTER_FIELDS.map((field) => (
                  <SelectItem key={field.value} value={field.value}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedField?.type === 'text' && (
              <TextInput
                placeholder={`Search ${selectedField.label.toLowerCase()}...`}
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                className="w-48"
                size="sm"
              />
            )}

            {selectedField?.type === 'select' && (
              <Select value={selectValue} onValueChange={setSelectValue}>
                <SelectTrigger className="w-48" size="sm">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {CREDENTIAL_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {selectedField?.type === 'date' && (
              <>
                <Select value={dateOperator} onValueChange={setDateOperator}>
                  <SelectTrigger className="w-28" size="sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_OPERATORS.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        {op.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Popover>
                  <Popover.Trigger asChild>
                    <button
                      type="button"
                      className="w-44 h-9.25 px-3 py-2 rounded bg-white text-sm font-normal border border-gray-300 flex flex-row items-center gap-2 hover:bg-gray-50"
                    >
                      <CalendarIcon className="h-4 w-4 shrink-0" />
                      <span>{dateValue ? format(dateValue, 'MMM d, yyyy') : 'Pick date'}</span>
                    </button>
                  </Popover.Trigger>
                  <Popover.Content variant="light" className="w-auto p-2">
                    <Calendar mode="single" selected={dateValue} onSelect={setDateValue} />
                  </Popover.Content>
                </Popover>

                {dateOperator === 'between' && (
                  <>
                    <span className="text-sm text-gray-500">and</span>
                    <Popover>
                      <Popover.Trigger asChild>
                        <button
                          type="button"
                          className="w-44 h-9.25 px-3 py-2 rounded bg-white text-sm font-normal border border-gray-300 flex flex-row items-center gap-2 hover:bg-gray-50"
                        >
                          <CalendarIcon className="h-4 w-4 shrink-0" />
                          <span>{dateValueEnd ? format(dateValueEnd, 'MMM d, yyyy') : 'Pick date'}</span>
                        </button>
                      </Popover.Trigger>
                      <Popover.Content variant="light" className="w-auto p-2">
                        <Calendar mode="single" selected={dateValueEnd} onSelect={setDateValueEnd} />
                      </Popover.Content>
                    </Popover>
                  </>
                )}
              </>
            )}

            <Button variant="secondary" size="sm" onClick={handleApplyFilter} className="rounded-md">
              Apply
            </Button>
            {filter && (
              <Button variant="tertiary" size="sm" onClick={handleClearFilter} className="rounded-md">
                Clear
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {viewMode === 'grid' && (
              <Select
                value={`${sortField}:${sortDirection}`}
                onValueChange={(value) => {
                  const [field, direction] = value.split(':') as [CredentialsSortField, 'asc' | 'desc'];
                  setSort(field, direction);
                }}
              >
                <SelectTrigger className="w-44" size="sm">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <ListViewSwitch viewMode={viewMode} onViewModeChange={setViewMode} />
          </div>
        </div>

        <SavedSearches
          searches={savedSearches}
          onApply={(search) => {
            setFilter(search.filter);
            if (search.sortField && search.sortDirection) {
              setSort(search.sortField as CredentialsSortField, search.sortDirection);
            }

            setActiveSearchName(search.name);
          }}
          onDelete={deleteSearch}
          onSave={saveSearch}
          currentFilter={filter}
          currentSortField={sortField}
          currentSortDirection={sortDirection}
          isNameTaken={isNameTaken}
          activeSearchName={activeSearchName}
        />

        {filter && (
          <div className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-md">
            <span className="font-medium">Active filter:</span>{' '}
            <code className="bg-gray-200 px-1 rounded">{filter}</code>
          </div>
        )}

        {viewMode === 'list' ? <CredentialTable /> : <CredentialGrid credentials={credentials} />}

        <Pagination
          totalSize={totalSize}
          pageSize={pageSize}
          currentPage={currentPage}
          totalPages={totalPages}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onPageSizeChange={setPageSize}
          onFirst={goToFirstPage}
          onPrevious={goToPreviousPage}
          onNext={goToNextPage}
        />
      </div>
    </div>
  );
};
